// Single source of truth for Log Monitor styling (Phase A — light mode only).
// Components must read colors from here, not hardcode hex. (Dark mode is
// intentionally out of scope: the admin shell + global.css are light-only.)

export const tokens = {
  surface: "#FFFFFF",
  elevated: "#FAFAFA",
  pageBg: "#F4F5F6",
  divider: "rgba(0,0,0,0.12)",
  textPrimary: "#1A1A1A",
  textSecondary: "#6B7280",
  textTertiary: "#9AA3AF",
  hover: "#F4F5F6",
  zebra: "#FAFAFA",
  mono: '"Roboto Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
};

// Placeholder/em-dash for empty cells (never render literal "--").
export const EMPTY = "—";

export type Severity = "error" | "warning" | "success" | "info" | "neutral";

// Severity tokens (light): text + soft background tint.
export const SEVERITY: Record<Severity, { text: string; bg: string }> = {
  error: { text: "#D32F2F", bg: "#FDECEC" },
  warning: { text: "#B26A00", bg: "#FBF0DD" },
  success: { text: "#2E7D32", bg: "#E8F3E8" },
  info: { text: "#1976D2", bg: "#E7F1FB" },
  neutral: { text: "#6B7280", bg: "#F1F3F4" },
};

// Log level -> severity. ERROR/WARN(ING)/INFO; anything else is neutral.
export function severityForLevel(level?: string | null): Severity {
  switch ((level || "").toUpperCase()) {
    case "ERROR":
      return "error";
    case "WARN":
    case "WARNING":
      return "warning";
    case "INFO":
      return "info";
    default:
      return "neutral";
  }
}

// Status / error code -> severity by HTTP class. Non-HTTP codes
// (ERR_BAD_REQUEST, ECONNABORTED, …) and blanks fall back to neutral/grey.
export function severityForCode(code?: string | number | null): Severity {
  if (code == null || code === "") return "neutral";
  const s = String(code);
  if (/^\d{3}$/.test(s)) {
    if (s[0] === "2") return "success";
    if (s[0] === "4") return "warning";
    if (s[0] === "5") return "error";
  }
  return "neutral";
}

// Short, human label for a level pill.
export function levelLabel(level?: string | null): string {
  switch ((level || "").toUpperCase()) {
    case "ERROR":
      return "Error";
    case "WARN":
    case "WARNING":
      return "Warn";
    case "INFO":
      return "Info";
    default:
      return level || "—";
  }
}
